/**
 * The loan tape's Needs you tab (DOP-627): what to look at before the tape
 * goes out, one card each, in the style of the monitoring and deal screening
 * Needs you lists.
 *
 * Two things can need a person today: the shown run failed, or a column came
 * out with nothing in the mapping behind it. Each card says why in words and
 * offers the one thing to do.
 */

import React from "react";
import type { LoanTapePreviewColumn } from "../Chat/Chat.types";
import RunFailureNote from "./RunFailureNote";
import type { RunFailure } from "./runFailure";

const BUTTON =
	"inline-flex h-9 items-center whitespace-nowrap rounded-lg border px-3.5 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008782] disabled:opacity-50";

export interface TapeNeedsYouProps {
	/** Why the shown run failed; `null` when it did not. */
	failure: RunFailure | null;
	columns: readonly LoanTapePreviewColumn[];
	canRun: boolean;
	onRun: () => void;
	/** Opens How it's set up on the files form: most failures are a file. */
	onChangeFiles: () => void;
	onOpenColumn: (column: LoanTapePreviewColumn) => void;
}

const TapeNeedsYou: React.FC<TapeNeedsYouProps> = ({
	failure,
	columns,
	canRun,
	onRun,
	onChangeFiles,
	onOpenColumn,
}) => {
	if (!failure && columns.length === 0) {
		return (
			<p className="rounded-xl border border-gray-200 bg-white px-4 py-6 text-center text-sm text-[#5B6472]">
				Nothing needs you: every column in this tape has a source in the new
				file.
			</p>
		);
	}

	return (
		<section className="space-y-3.5">
			<p className="text-sm text-[#5B6472]">
				Look at these before you send the tape out.
			</p>
			{failure && (
				<section
					aria-label="The run failed"
					className="flex items-start gap-3 rounded-xl border border-[#fecdca] bg-[#fffbfa] px-[18px] py-3.5"
				>
					<div className="min-w-0 flex-1">
						<p className="text-[15px] font-semibold text-[#111111]">
							This run failed
						</p>
						<div className="mt-1 text-[#b42318]">
							<RunFailureNote failure={failure} />
						</div>
					</div>
					<div className="flex flex-shrink-0 flex-col gap-2 sm:flex-row">
						<button
							type="button"
							onClick={onChangeFiles}
							disabled={!canRun}
							className={`${BUTTON} border-[#004C4B] bg-[#004C4B] font-semibold text-white hover:bg-[#003a3a]`}
						>
							Change the files
						</button>
						<button
							type="button"
							onClick={onRun}
							disabled={!canRun}
							className={`${BUTTON} border-[#99CFCD] bg-white text-[#004C4B] hover:bg-[#f6fbfb]`}
						>
							Try again
						</button>
					</div>
				</section>
			)}
			{columns.map((column) => (
				<section
					key={column.header}
					aria-label={column.header}
					className="rounded-xl border border-gray-200 bg-white"
				>
					<div className="flex items-center gap-3 px-[18px] py-3.5">
						<div className="min-w-0 flex-1">
							<p className="text-[15px] font-semibold text-[#111111]">
								{column.header}
							</p>
							<p className="mt-0.5 text-xs text-[#6B7280]">
								Column in your tape
							</p>
						</div>
						<button
							type="button"
							onClick={() => onOpenColumn(column)}
							className={`${BUTTON} border-[#004C4B] bg-[#004C4B] font-semibold text-white hover:bg-[#003a3a]`}
						>
							See the column
						</button>
					</div>
					<div className="flex items-center gap-2.5 border-t border-[#f1f2f4] px-[18px] py-2.5 text-[13px] text-[#3f4247]">
						<span className="w-44 flex-shrink-0 text-[11px] font-bold uppercase tracking-[0.06em] text-[#b54708]">
							No source found
						</span>
						Doppler couldn't find where this comes from in the new file. Make
						sure the example pair has it.
					</div>
				</section>
			))}
		</section>
	);
};

export default TapeNeedsYou;
